import React from 'react';

import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ExclamationIcon from '@/components/UI/Icons/ExclamationIcon';
import Tooltip from '@/components/UI/Tooltip';
import HourglassIcon from '@/components/UI/Icons/HourglassIcon';
import Spinner from '@/components/UI/Spinner';
import { VerificationStatus } from '@lucky7ventures/bff-types';

const ApprovalStatus = styled.span`
  font-family: 'Karla', sans-serif;
  font-size: 12px;
  border-radius: ${props => props.theme.borderRadiusPill};
  display: inline-flex;
  align-items: center;
  font-weight: bold;

  .icon {
    position: relative;
    display: inline-flex;
    margin-left: 6px;
    background: ${props => props.theme.colors.grey50};
    color: ${props => props.theme.colors.white};
    padding: 2px;
    border-radius: ${props => props.theme.borderRadiusPill};
    cursor: pointer;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  &.approved {
    .icon {
      background: ${props => props.theme.colors.green};
    }
  }

  &.rejected {
    .icon {
      background: ${props => props.theme.colors.red};
    }
  }

  &.pending {
    .icon {
      background: ${props => props.theme.colors.yellow};
    }
  }
`;

export function KycStatusIcon({ id }: { id: VerificationStatus }): JSX.Element {
  const intl = useIntl();

  if (id === VerificationStatus.Approved) {
    return (
      <ApprovalStatus className="approved">
        <span className="icon">
          <CheckIcon />
        </span>
      </ApprovalStatus>
    );
  }
  if (
    [
      VerificationStatus.Requested,
      VerificationStatus.UserRequested,
      VerificationStatus.PendingRequest,
    ].includes(id)
  ) {
    return (
      <ApprovalStatus className="not-set">
        <span className="icon">
          <ExclamationIcon />
        </span>
      </ApprovalStatus>
    );
  }
  if (id === VerificationStatus.Rejected) {
    return (
      <ApprovalStatus className="rejected">
        <span className="icon tooltip-container">
          <XMarkIcon />
          <Tooltip
            width="160px"
            text={intl.formatMessage({ id: 'settings.verify.rejected.tooltip' })}
            position="top-right"
          />
        </span>
      </ApprovalStatus>
    );
  }
  if (id === VerificationStatus.Processing) {
    return (
      <ApprovalStatus className="pending">
        <span className="icon tooltip-container">
          <HourglassIcon />
          <Tooltip
            width="160px"
            text={intl.formatMessage({ id: 'settings.verify.pending.tooltip' })}
            position="top-right"
          />
        </span>
      </ApprovalStatus>
    );
  }

  return (
    <ApprovalStatus className="not-set">
      <Spinner height={12} width={12} borderwidth={2} margin="0" color="white" />
    </ApprovalStatus>
  );
}
